/**
 * What one physician-week holds on the admin's availability grid, and how a
 * physician's request stands on their own screens.
 *
 * A week can hold two different things, and they are kept apart on purpose:
 * - the admin's entry (vacation, leave, preferred or avoid), which counts as
 *   entered, needs no approval, and wins over any request for that week;
 * - the physician's request (vacation, preferred or avoid), which counts
 *   only once approved, and may have been declined.
 * Changing one never changes the other: clearing the admin's entry leaves
 * the request as it was, and declining a request is always its own action.
 *
 * Pure, so it is tested without a browser.
 */
export type RequestStatus = 'pending' | 'approved' | 'declined';

export type EntryKind = 'vacation' | 'loa' | 'preferred' | 'avoid';

/** One row of the grid's data, as the server sends it. */
export interface GridCell {
  physicianId: string;
  weekStart: string;
  kind: EntryKind;
  source: 'admin' | 'request';
  status?: RequestStatus;
  requestId?: string;
}

export interface CellRequest {
  id: string;
  kind: EntryKind;
  status: RequestStatus;
}

export interface CellState {
  /** The admin's entry for the week, if any. */
  entry: EntryKind | null;
  /** The physician's requests for the week (almost always one): approved first, then awaiting approval, then declined. */
  requests: CellRequest[];
  /** What the cell shows: the admin's entry, or else the request that matters most. */
  shown: { kind: EntryKind; source: 'entry' | RequestStatus } | null;
  /** An admin's entry sits over a request that is not declined, so that request does not count while the entry is there. */
  overRequest: boolean;
}

const STATUS_ORDER: Record<RequestStatus, number> = { approved: 0, pending: 1, declined: 2 };

export function cellKey(physicianId: string, weekStart: string): string {
  return `${physicianId}|${weekStart}`;
}

export const EMPTY_CELL: CellState = { entry: null, requests: [], shown: null, overRequest: false };

/** Every physician-week that holds anything, keyed by cellKey. */
export function cellStates(cells: readonly GridCell[]): Map<string, CellState> {
  const grouped = new Map<string, { entry: EntryKind | null; requests: CellRequest[] }>();
  for (const cell of cells) {
    const key = cellKey(cell.physicianId, cell.weekStart);
    const cur = grouped.get(key) ?? { entry: null, requests: [] };
    if (cell.source === 'admin') cur.entry = cell.kind;
    else if (cell.requestId !== undefined && cell.status !== undefined) cur.requests.push({ id: cell.requestId, kind: cell.kind, status: cell.status });
    grouped.set(key, cur);
  }
  const out = new Map<string, CellState>();
  for (const [key, { entry, requests }] of grouped) {
    requests.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
    const top = requests[0];
    out.set(key, {
      entry,
      requests,
      shown: entry !== null ? { kind: entry, source: 'entry' } : top ? { kind: top.kind, source: top.status } : null,
      overRequest: entry !== null && requests.some((r) => r.status !== 'declined'),
    });
  }
  return out;
}

/** How a physician's request stands on their own screens: an admin's entry for the same week replaces it, whatever its decision. */
export type RequestStanding = RequestStatus | 'replaced';

export function requestStanding(status: RequestStatus, entryForWeek: boolean): RequestStanding {
  return entryForWeek ? 'replaced' : status;
}
